import { useEffect, useState, type ReactElement } from 'react';
import { MaterialIcon } from '@lapo/ui';
import { displayTitle } from './noteIndex.js';
import { readBacklinksCache, writeBacklinksCache } from './backlinksCache.js';
import type { ApiClient, BacklinkGroupDTO } from './auth/apiClient.js';

/**
 * The Logseq "Linked References" section (IMPL-07). Shown at the bottom of a page, it lists every
 * block elsewhere in the graph that links to this page — `[[Name]]` and `#tag` references — grouped
 * by source note (a card per note, newest-first) with the linking block's text as context. Clicking
 * a card opens that source note. Bi-directional linking made visible.
 *
 * Data comes from `GET /pages/:noteId/backlinks`, which is paged and searchable: a heavily-linked
 * page (hundreds of sources) loads {@link PAGE} cards at a time behind a "Show more" button, a search
 * box filters by source title / reference context, and the first page is cached per note so switching
 * back paints instantly (store-and-hydrate). When the API is unconfigured or the call fails it renders
 * nothing — the page still works offline.
 */

/** Cards fetched per page (and per "Show more"). */
const PAGE = 50;

/** One run of a reference's context: plain text, or a link the user can click to jump to its page. */
type ContextSeg =
  | { kind: 'text'; text: string }
  | { kind: 'wikilink' | 'tag'; name: string; label: string };

// Split a context string into text + link segments. The server spells links out as `[[target]]`,
// `[[target|label]]`, and `#tag` (see core/blockContext), which we turn back into clickable chips.
const REF_RE = /\[\[([^\]|]+?)(?:\|([^\]]+?))?\]\]|#([A-Za-z0-9_/-]+)/g;
function parseContext(s: string): ContextSeg[] {
  const segs: ContextSeg[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  REF_RE.lastIndex = 0;
  while ((m = REF_RE.exec(s)) !== null) {
    if (m.index > last) segs.push({ kind: 'text', text: s.slice(last, m.index) });
    if (m[1] != null) segs.push({ kind: 'wikilink', name: m[1], label: m[2] ?? m[1] });
    else segs.push({ kind: 'tag', name: m[3]!, label: `#${m[3]}` });
    last = m.index + m[0].length;
  }
  if (last < s.length) segs.push({ kind: 'text', text: s.slice(last) });
  return segs;
}

type Props = {
  api: ApiClient;
  noteId: string;
  /** The page's title, so a local-only page (not yet in the registry) still resolves server-side. */
  name?: string;
  onOpenNote: (noteId: string) => void;
  /** Jump to a `[[wikilink]]`/`#tag` clicked inside a reference's context (resolves the name to a page). */
  onNavigate: (target: { kind: 'wikilink' | 'tag'; name: string }) => void;
  /** Bump to refetch (e.g. after the page's own edits are reindexed). */
  reloadKey?: number;
};

// Remount the inner view per note (key=noteId): its state initializes from that note's cache, so
// switching pages either paints the cached first page instantly or blanks — never the old page's refs.
export function LinkedReferences(props: Props): ReactElement {
  return <LinkedReferencesInner key={props.noteId} {...props} />;
}

function LinkedReferencesInner({ api, noteId, name, onOpenNote, onNavigate, reloadKey }: Props): ReactElement | null {
  const cached = readBacklinksCache(noteId);
  const [groups, setGroups] = useState<BacklinkGroupDTO[]>(cached?.groups ?? []);
  const [total, setTotal] = useState(cached?.total ?? 0);
  const [totalGroups, setTotalGroups] = useState(cached?.totalGroups ?? 0);
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(cached == null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Once the default (unfiltered) view has loaded we know whether this page has any references at all,
  // which decides whether the whole section (and its search box) should exist.
  const [hasAny, setHasAny] = useState(cached != null && cached.totalGroups > 0);

  // Debounce the search box so each keystroke doesn't hit the server.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  // (Re)load the first page whenever the query (or title / reindex signal) changes. Replaces the list.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void api
      .getBacklinks(noteId, name, { limit: PAGE, offset: 0, query: debounced || undefined })
      .then((d) => {
        if (cancelled) return;
        setGroups(d.groups);
        setTotal(d.total);
        setTotalGroups(d.totalGroups);
        setLoading(false);
        if (!debounced) {
          setHasAny(d.totalGroups > 0);
          writeBacklinksCache(noteId, d); // cache only the default view (offset 0, no query)
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false); // keep any cached groups on a failed refresh
      });
    return () => {
      cancelled = true;
    };
  }, [api, noteId, name, debounced, reloadKey]);

  const loadMore = (): void => {
    setLoadingMore(true);
    void api
      .getBacklinks(noteId, name, { limit: PAGE, offset: groups.length, query: debounced || undefined })
      .then((d) => {
        setGroups((prev) => [...prev, ...d.groups]);
        setTotal(d.total);
        setTotalGroups(d.totalGroups);
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  };

  // Nothing to show: a page with no references and no active search collapses away entirely.
  if (!hasAny && !debounced && !loading) return null;
  // First load with no cached page yet — stay out of the layout until we have something to render.
  if (!hasAny && groups.length === 0 && loading && !debounced) return null;

  const remaining = totalGroups - groups.length;

  return (
    <section className="lapo-linkrefs">
      <button
        type="button"
        className="lapo-linkrefs-head"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <MaterialIcon name={collapsed ? 'chevron_right' : 'expand_more'} size={18} />
        <span>
          {total} Linked Reference{total === 1 ? '' : 's'}
          {debounced ? ' matching' : ''}
        </span>
      </button>
      {!collapsed && (
        <div className="lapo-linkrefs-body">
          {/* Show the search box once a page has enough references that scanning by eye is painful. */}
          {(hasAny || debounced) && (totalGroups > 8 || debounced) && (
            <input
              type="search"
              className="lapo-linkrefs-search"
              placeholder="Search references…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search linked references"
            />
          )}
          {groups.map((g) => (
            <article key={g.sourceNoteId} className="lapo-linkref-card">
              <button type="button" className="lapo-linkref-src" onClick={() => onOpenNote(g.sourceNoteId)}>
                {/* Journals read as their date (e.g. "Nov 25th, 2024"); documents as their title. */}
                {displayTitle({ noteId: g.sourceNoteId, title: g.sourceTitle })}
              </button>
              <ul className="lapo-linkref-list">
                {g.refs.map((r, i) => (
                  <li key={i} className="lapo-linkref-item">
                    {parseContext(r.context).map((seg, j) =>
                      seg.kind === 'text' ? (
                        <span key={j}>{seg.text}</span>
                      ) : (
                        <button
                          key={j}
                          type="button"
                          className="lapo-linkref-link"
                          onClick={() => onNavigate({ kind: seg.kind, name: seg.name })}
                        >
                          {seg.label}
                        </button>
                      ),
                    )}
                  </li>
                ))}
              </ul>
            </article>
          ))}
          {debounced && !loading && groups.length === 0 && (
            <p className="lapo-linkrefs-empty">No references match “{debounced}”.</p>
          )}
          {remaining > 0 && (
            <button type="button" className="lapo-linkrefs-more" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : `Show ${Math.min(PAGE, remaining)} more (${remaining} left)`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
